/**
 * Reverse charge on the 3B screen (W3-18 (5), D-105, D-106).
 *
 * 3.1(d) has three lines since 2026-10-14 — what the GSTR-2B's suppliers declared, purchases the
 * books mark reverse charge from a supplier with no GSTIN, and imports of services in the books —
 * and the credit on them is claimed in the same month, in 4(A)(3) and 4(A)(2). The service sends
 * them on the cells (`lines`, `ledger`, `of_which`); these turn them into the rows the form shows
 * under its own, in the form's words. A line nobody declared carries a state, never a zero.
 */

export const RCM_HEADS = ['igst', 'cgst', 'sgst', 'cess'] as const

export interface SubRow {
  key: string
  label: string
  /** Paise per column; absent when the row states rather than counts. */
  values?: Record<string, number>
  /** Said instead of figures. */
  state?: string
}

type Cell = Record<string, unknown> | undefined

const LINE_LABELS: Array<[string, string]> = [
  ['2b', 'of which — declared by registered suppliers in the GSTR-2B'],
  ['unregistered', 'of which — purchases from suppliers with no GSTIN (books)'],
  ['import_of_services', 'of which — imports of services (books)'],
]

function figures(raw: unknown): Record<string, number> {
  const from = (raw ?? {}) as Record<string, unknown>
  const out: Record<string, number> = {}
  for (const key of ['taxable', ...RCM_HEADS]) {
    const v = from[key]
    out[key] = typeof v === 'number' ? v : 0
  }
  return out
}

/** 3.1(d)'s three lines, and the client's GST ledger beside them when it was supplied. */
export function reverseChargeLines(cell: Cell): SubRow[] {
  const lines = cell?.lines as Record<string, Record<string, unknown>> | undefined
  const rows: SubRow[] = []
  if (lines) {
    for (const [key, label] of LINE_LABELS) {
      const line = lines[key]
      if (!line || typeof line.state === 'string') {
        rows.push({ key, label, state: key === '2b' ? 'Not declared' : 'None marked' })
      } else {
        rows.push({ key, label, values: figures(line) })
      }
    }
  }
  if (cell?.ledger) {
    rows.push({
      key: 'ledger',
      label: "Memo — in the client's GST ledger (a check, not added)",
      values: figures(cell.ledger),
    })
  }
  return rows
}

/** 4(A)(2) and 4(A)(3), the reverse-charge credit of the month. None while 4(A) states. */
export function reverseChargeCredit(cell: Cell): SubRow[] {
  const parts = cell?.of_which as Record<string, unknown> | undefined
  if (!parts || typeof cell?.state === 'string') return []
  return [
    { key: '4A(2)', label: '(2) Import of services', values: figures(parts['4A(2)']) },
    {
      key: '4A(3)',
      label: '(3) Inward supplies liable to reverse charge',
      values: figures(parts['4A(3)']),
    },
  ]
}
